import { Box, rem, Text } from '@mantine/core'
import { useCallback, useEffect, useRef, useState } from 'react'

import { goldTileDefinition } from '~/boggle/config/specialTiles/definitions/gold'
import { easeOutSoft } from '~/boggle/ui/atoms/RampingNumberText'
import { RUN_RESULTS_SCORE_RAMP_BASE_MS } from '~/boggle/ui/run/runResultsScoreRamp'

import classes from './GoldTilesRunTracker.module.css'

const TRACKER_SIZE = rem(72)
const COUNT_FONT_SIZE = rem(22)
const VIEW_BOX = 100
const PULSE_STAGGER_MS = 120
const PULSE_DURATION_MS = 580
const BASE_HEX_RADIUS_RATIO = 0.38

/** After the count reaches 0, hold before the opacity fade begins. */
const PRE_FADE_HOLD_AT_ZERO_MS = 300
/** Quick fade-out applied once the drain completes — softens the moment the tracker disappears. */
const POST_DRAIN_FADE_MS = 220
/** Pre-hold "puff": scale 1 → {@link RUN_SUMMARY_GOLD_SCORE_PEAK_SCALE} + glow up over this duration. */
const SCALE_UP_MS = 200
/** Run-summary scale-up puff: slide 30px right and 20px down over the same beat. */
const SCALE_UP_ENTER_OFFSET_X_PX = 30
const SCALE_UP_ENTER_OFFSET_Y_PX = 20
/**
 * Peak scale while the run-summary gold score sequence holds (after puff, before drain).
 * Larger than in-run play so the "being tallied" moment reads clearly at the same layout size.
 */
const RUN_SUMMARY_GOLD_SCORE_PEAK_SCALE = 1.6
/** Final scale at the end of the drain — slight deflate sells the value transfer. */
const END_SCALE = 1.4
const BOOSTED_GLOW = 'drop-shadow(0 0 24px rgba(246, 221, 138, 0.9))'
const RESTING_GLOW = 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.3))'
/** Room for peak-scale glow + drop-shadow so nearby overflow clips less aggressively. */
const GLOW_BLEED_PX = 32

function hexagonPoints(viewBoxSize: number): string {
  const sides = 6
  const radius = viewBoxSize * BASE_HEX_RADIUS_RATIO
  const center = viewBoxSize / 2
  const rotation = -Math.PI / 2

  return Array.from({ length: sides }, (_, index) => {
    const angle = rotation + (Math.PI * 2 * index) / sides
    const x = center + Math.cos(angle) * radius
    const y = center + Math.sin(angle) * radius

    return `${x},${y}`
  }).join(' ')
}

const HEX_POINTS = hexagonPoints(VIEW_BOX)

function clamp01(t: number) {
  return Math.min(1, Math.max(0, t))
}

/**
 * Drain animation request — when `key` changes, the tracker boosts its glow,
 * waits `preDelayMs`, then drains the count + glow to zero over `drainDurationMs`,
 * fires `onComplete`, and hides itself.
 */
export type GoldTrackerDrainAnimation = {
  key: number | string
  preDelayMs?: number
  drainDurationMs?: number
  onComplete?: () => void
}

/**
 * Drain phase progression:
 *   idle → scaleUp (200ms, 1→~1.92x + glow up) → hold (300ms, glow holds at peak)
 *        → draining (… count → 0) → 300ms hold at 0 → fadingOut (220ms opacity) → hidden
 */
type DrainPhase = 'idle' | 'scaleUp' | 'hold' | 'draining' | 'fadingOut' | 'hidden'

export function GoldTilesRunTracker({
  forceHidden = false,
  inPlayPulseEnabled = true,
  inPlaySpringEnabled = true,
  goldTilesUsed,
  drainAnimation,
}: {
  /** When true, omits the tracker (e.g. user skipped summary scoring choreography). */
  forceHidden?: boolean
  /** When true, shows expanding pulse rings on in-play increments. */
  inPlayPulseEnabled?: boolean
  /** When true, runs the tracker spring animation on in-play increments. */
  inPlaySpringEnabled?: boolean
  goldTilesUsed: number
  /** When provided and its `key` changes, kicks off the drain-to-zero animation. */
  drainAnimation?: GoldTrackerDrainAnimation | null
}) {
  const prevGoldRef = useRef<number | null>(null)
  const [pulseIds, setPulseIds] = useState<number[]>([])
  const nextPulseId = useRef(0)
  const [springActive, setSpringActive] = useState(false)
  const inPlayPulseEnabledRef = useRef(inPlayPulseEnabled)
  const inPlaySpringEnabledRef = useRef(inPlaySpringEnabled)

  const [drainPhase, setDrainPhase] = useState<DrainPhase>('idle')
  /** One frame at translate(0, 0) before the scale-up slide transition runs. */
  const [scaleUpEnterFromOrigin, setScaleUpEnterFromOrigin] = useState(false)
  /** Currently displayed count — driven by RAF during `draining`, otherwise mirrors `goldTilesUsed`. */
  const [displayCount, setDisplayCount] = useState(goldTilesUsed)
  const onCompleteRef = useRef<(() => void) | undefined>(undefined)

  useEffect(() => {
    onCompleteRef.current = drainAnimation?.onComplete
  }, [drainAnimation?.onComplete])

  useEffect(() => {
    inPlayPulseEnabledRef.current = inPlayPulseEnabled
    inPlaySpringEnabledRef.current = inPlaySpringEnabled
  }, [inPlayPulseEnabled, inPlaySpringEnabled])

  // When *not* in a drain sequence, the displayed count tracks live `goldTilesUsed`.
  useEffect(() => {
    if (drainPhase === 'idle') {
      setDisplayCount(goldTilesUsed)
    }
  }, [drainPhase, goldTilesUsed])

  // In-play pulse rings when a new gold tile is used. Skipped while draining.
  useEffect(() => {
    if (prevGoldRef.current === null) {
      prevGoldRef.current = goldTilesUsed
      return
    }

    const prev = prevGoldRef.current
    prevGoldRef.current = goldTilesUsed
    const delta = goldTilesUsed - prev
    if (delta <= 0 || drainPhase !== 'idle') {
      return
    }

    if (inPlaySpringEnabledRef.current) {
      // Trigger at most once per value change, even when delta > 1.
      setSpringActive(false)
      requestAnimationFrame(() => setSpringActive(true))
    }

    if (!inPlayPulseEnabledRef.current) {
      return
    }

    const timeouts: number[] = []
    for (let i = 0; i < delta; i++) {
      const handle = window.setTimeout(() => {
        const id = nextPulseId.current++
        setPulseIds(rows => [...rows, id])
      }, i * PULSE_STAGGER_MS)
      timeouts.push(handle)
    }

    return () => {
      timeouts.forEach(clearTimeout)
    }
  }, [goldTilesUsed, drainPhase])

  const dismissPulse = useCallback((id: number) => {
    setPulseIds(rows => rows.filter(row => row !== id))
  }, [])

  // Drain sequence: scaleUp → hold → drain (count + scale + glow) → fade out → hide.
  const drainKey = drainAnimation?.key ?? null
  const drainPreDelayMs = drainAnimation?.preDelayMs ?? 300
  const drainDurationMs = drainAnimation?.drainDurationMs ?? RUN_RESULTS_SCORE_RAMP_BASE_MS
  useEffect(() => {
    if (drainKey === null) {
      setDrainPhase('idle')
      setScaleUpEnterFromOrigin(false)
      setPulseIds([])
      return
    }

    const startCount = goldTilesUsed
    setDrainPhase('scaleUp')
    setScaleUpEnterFromOrigin(true)
    setDisplayCount(startCount)
    setPulseIds([])

    let slideEnterRaf: number | null = null
    let raf: number | null = null
    let holdTimer: number | null = null
    let pauseAtZeroTimer: number | null = null
    let hideTimer: number | null = null

    slideEnterRaf = requestAnimationFrame(() => {
      slideEnterRaf = requestAnimationFrame(() => {
        setScaleUpEnterFromOrigin(false)
      })
    })

    const scaleUpTimer = window.setTimeout(() => {
      setDrainPhase('hold')
      holdTimer = window.setTimeout(() => {
        setDrainPhase('draining')
        const startTime =
          typeof performance !== 'undefined' ? performance.now() : Date.now()

        const tick = (now: number) => {
          const elapsed = now - startTime
          const t = drainDurationMs <= 0 ? 1 : clamp01(elapsed / drainDurationMs)
          const eased = easeOutSoft(t)
          setDisplayCount(startCount * (1 - eased))

          if (t < 1) {
            raf = requestAnimationFrame(tick)
            return
          }

          setDisplayCount(0)
          pauseAtZeroTimer = window.setTimeout(() => {
            setDrainPhase('fadingOut')
            hideTimer = window.setTimeout(() => {
              setDrainPhase('hidden')
              onCompleteRef.current?.()
            }, POST_DRAIN_FADE_MS)
          }, PRE_FADE_HOLD_AT_ZERO_MS)
        }
        raf = requestAnimationFrame(tick)
      }, drainPreDelayMs)
    }, SCALE_UP_MS)

    return () => {
      window.clearTimeout(scaleUpTimer)
      if (holdTimer !== null) {
        window.clearTimeout(holdTimer)
      }
      if (pauseAtZeroTimer !== null) {
        window.clearTimeout(pauseAtZeroTimer)
      }
      if (hideTimer !== null) {
        window.clearTimeout(hideTimer)
      }
      if (slideEnterRaf !== null) {
        cancelAnimationFrame(slideEnterRaf)
      }
      if (raf !== null) {
        cancelAnimationFrame(raf)
      }
    }
  }, [drainKey, drainPreDelayMs, drainDurationMs, goldTilesUsed])

  if (forceHidden || drainPhase === 'hidden') {
    return null
  }

  const isFadingOut = drainPhase === 'fadingOut'
  const renderedCount = Math.ceil(displayCount)

  // Phase-derived visual targets. CSS transitions interpolate between renders.
  const scale =
    drainPhase === 'scaleUp' || drainPhase === 'hold'
      ? RUN_SUMMARY_GOLD_SCORE_PEAK_SCALE
      : drainPhase === 'draining' || drainPhase === 'fadingOut'
        ? END_SCALE
        : 1
  const filter =
    drainPhase === 'scaleUp' || drainPhase === 'hold' ? BOOSTED_GLOW : RESTING_GLOW
  // Pick a transition duration matching the current animating phase so the
  // simultaneous property change is interpolated rather than snapped.
  const motionMs =
    drainPhase === 'scaleUp'
      ? SCALE_UP_MS
      : drainPhase === 'draining'
        ? drainDurationMs
        : 0
  const transition = `transform ${motionMs}ms ease-out, filter ${motionMs}ms ease-out, opacity ${POST_DRAIN_FADE_MS}ms ease-out`
  const isIdle = drainPhase === 'idle'
  const isScaleUpEntering = drainPhase === 'scaleUp' && scaleUpEnterFromOrigin
  const hasSummarySlideOffset = drainPhase !== 'idle' && !isScaleUpEntering
  const slideX = hasSummarySlideOffset ? SCALE_UP_ENTER_OFFSET_X_PX : 0
  const slideY = hasSummarySlideOffset ? SCALE_UP_ENTER_OFFSET_Y_PX : 0
  const renderedScale = isScaleUpEntering ? 1 : scale

  return (
    <Box
      aria-label={`Gold tiles played this run: ${goldTilesUsed}`}
      pos="relative"
      style={{
        height: `calc(${TRACKER_SIZE} + ${rem(GLOW_BLEED_PX * 2)})`,
        margin: rem(-GLOW_BLEED_PX),
        overflow: 'visible',
        padding: rem(GLOW_BLEED_PX),
        pointerEvents: 'none',
        width: `calc(${TRACKER_SIZE} + ${rem(GLOW_BLEED_PX * 2)})`,
      }}
    >
      <Box
        className={isIdle && springActive ? classes.trackerSpring : undefined}
        h={TRACKER_SIZE}
        onAnimationEnd={
          isIdle && springActive
            ? () => {
                setSpringActive(false)
              }
            : undefined
        }
        pos="relative"
        style={{
          overflow: 'visible',
          opacity: isFadingOut ? 0 : 1,
          transform:
            isIdle && !springActive
              ? 'scale(1)'
              : isIdle
                ? undefined
                : `translate3d(${slideX}px, ${slideY}px, 0) scale(${renderedScale})`,
          transformOrigin: '50% 50%',
          filter,
          transition: isIdle ? `filter 120ms ease-out` : isScaleUpEntering ? 'none' : transition,
        }}
        w={TRACKER_SIZE}
      >
      <svg
        aria-hidden
        height={TRACKER_SIZE}
        style={{ display: 'block', overflow: 'visible' }}
        viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}
        width={TRACKER_SIZE}
      >
        <polygon
          fill={goldTileDefinition.previewFill}
          points={HEX_POINTS}
          stroke="rgba(255,255,255,0.22)"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <g style={{ pointerEvents: 'none' }}>
          {pulseIds.map(id => (
            <g
              key={id}
              className={classes.pulseRing}
              onAnimationEnd={() => dismissPulse(id)}
            >
              <polygon
                fill="none"
                points={HEX_POINTS}
                stroke="#f6dd8a"
                strokeLinejoin="round"
                strokeWidth="3"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ))}
        </g>
      </svg>
      {/* HTML overlay for the count — gives us smooth ramping during drain. */}
      <Box
        pos="absolute"
        inset={0}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text
          ff="'Trebuchet MS', 'Segoe UI', sans-serif"
          fz={COUNT_FONT_SIZE}
          fw={700}
          style={{
            lineHeight: 1,
            color: drainPhase === 'idle' ? goldTileDefinition.previewTextColor : '#ffffff',
            transition: `color ${SCALE_UP_MS}ms ease-out`,
          }}
        >
          {renderedCount}
        </Text>
      </Box>
      </Box>
    </Box>
  )
}
